/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import req from '@sportninja/common/api/request'
import { t } from '@sportninja/common/i18n'
import { ENTITY_TYPES } from '@sportninja/common/sagas/utils'
import { resolveAssetUrl } from '@sportninja/common/utils/assetUrl'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import * as csv from 'csvtojson'
import dayjs from 'dayjs'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Table, Alert, Spin, AutoComplete } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { SearchOutlined, DownOutlined } from '@ant-design/icons'
import { SelectInput } from '../ts-components/SelectInput'
import RangePicker from '../ts-components/RangePicker'
import Button from '../ts-components/Button'
import Icon from '../Icon'
import useWindowSize from 'src/hooks/useWindowSize'
import { getTableStyles } from 'src/styles/tableStyles'
import Text from '../ts-components/Text'
import { paragraphSmall, weightRegular } from '../css'

const SCROLL_THRESHOLD = 500
const PAGE_SIZE = 50
const ALL_VALUE = 'ALL'
const COMPETITION_PLACEHOLDER =
  '/images/generic_placeholders/placeholder_competition.png'

/**
 * Format a date range for display
 */
const formatDateRange = (startsAt, endsAt) => {
  const start = dayjs(startsAt).format('MMM D, YYYY')
  const end = dayjs(endsAt).format('MMM D, YYYY')
  return `${start} - ${end}`
}

const filterAutoComplete = css`
  ${paragraphSmall}
  ${weightRegular}
  width: 100%;
  height: 40px !important;
  color: ${colors.WHITE};

  .ant-select-selector {
    background: transparent !important;
    border-radius: 8px !important;
    height: 40px !important;
  }

  .ant-select-selection-search {
    display: flex;
    align-items: center;
  }

  .ant-select-selection-search-input {
    height: 38px !important;
    color: ${colors.WHITE} !important;
    text-overflow: ellipsis !important;
    overflow: hidden !important;
    white-space: nowrap !important;
  }

  .ant-select-selection-placeholder {
    line-height: 38px !important;
    font-size: 14px !important;
    color: ${colors.NEUTRAL_50} !important;
  }

  .ant-select-clear {
    color: ${colors.WHITE};
  }

  &.ant-select-disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const optionImageStyle = css`
  width: 24px;
  height: 24px;
  border-radius: 4px;
  object-fit: cover;
  flex-shrink: 0;
  border: 1px solid ${colors.NEUTRAL_50};
`

// Generic function to transform CSV data to a consistent format
const transformData = (dataArray, reportType) => {
  if (!dataArray || !dataArray.length) {
    return []
  }
  return dataArray.map((item) => {
    const transformedItem = {}

    // Process each field in the CSV row
    Object.keys(item).forEach((key) => {
      // Preserve the original header title as the key
      transformedItem[key] = item[key]

      // Special case handling based on report type
      if (reportType === 'teamEngagement' && key === 'Has Logo') {
        // Convert '1' to true for 'Has Logo' field
        transformedItem[key] = item[key] === '1'
      }
    })

    return transformedItem
  })
}

const dropDownOptions = [
  {
    label: 'Penalty Report',
    value: 'penaltiesByTeam',
  },
  {
    label: 'Games Played',
    value: 'gamesPlayed',
  },
  {
    label: 'Team Engagement',
    value: 'teamEngagement',
  },
  // {
  // label: 'Registration Participant Info',
  // value: 'registrations',
  // },
  {
    label: 'Registration - Revenue Report',
    value: 'registrationRevenue',
  },
  {
    label: 'Registration - Revenue & Payment Summary',
    value: 'financialSummary',
  },
  {
    label: 'Registration - Transaction Report',
    value: 'registrationTransactions',
  },
  {
    label: 'Registration - Team Registrations',
    value: 'teamRegistrations',
  },
  {
    label: 'Registration - Player Registrations',
    value: 'playerRegistrations',
  },
  {
    label: 'Registration - Past Due Payments Report',
    value: 'pastDuePayments',
  },
  {
    label: 'Registration - Incomplete Registrations',
    value: 'incompleteRegistrations',
  },
  {
    label: 'Roster - Players',
    value: 'teamPlayers',
  },
  {
    label: 'Roster - Team Officials',
    value: 'teamOfficials',
  },
  {
    label: 'Officials - Games Played',
    value: 'officialsGamesPlayed',
  },
  {
    label: 'Officials - Games Assigned',
    value: 'officialsGamesAssigned',
  },
]

const reportEndpoints = {
  penaltiesByTeam: '/reports/:entityId/team_penalty_minutes',
  registrations: '/reports/:entityId/registration',
  gamesPlayed: '/reports/:entityId/games_played',
  teamEngagement: '/reports/:entityId/team_engagement',
  registrationRevenue: '/reports/:entityId/registration',
  financialSummary: '/reports/:entityId/registration',
  registrationTransactions: '/reports/:entityId/registration',
  teamRegistrations: '/reports/:entityId/registration',
  playerRegistrations: '/reports/:entityId/registration',
  pastDuePayments: '/reports/:entityId/registration',
  incompleteRegistrations: '/reports/:entityId/registration',
  teamPlayers: '/reports/:entityId/team/players',
  teamOfficials: '/reports/:entityId/team/team_officials',
  officialsGamesPlayed: '/reports/:entityId/official/games_played',
  officialsGamesAssigned: '/reports/:entityId/official/games_assigned',
}

const reportOrgEndpoints = {
  penaltiesByTeam: '/reports/org/:entityId/team_penalty_minutes',
  registrations: '/reports/org/:entityId/registration',
  gamesPlayed: '/reports/org/:entityId/games_played',
  financialSummary: '/reports/org/:entityId/registration',
  teamEngagement: '/reports/org/:entityId/team_engagement',
  registrationRevenue: '/reports/org/:entityId/registration',
  registrationTransactions: '/reports/org/:entityId/registration',
  teamRegistrations: '/reports/org/:entityId/registration',
  playerRegistrations: '/reports/org/:entityId/registration',
  pastDuePayments: '/reports/org/:entityId/registration',
  incompleteRegistrations: '/reports/org/:entityId/registration',
  teamPlayers: '/reports/org/:entityId/team/players',
  teamOfficials: '/reports/org/:entityId/team/team_officials',
  officialsGamesPlayed: '/reports/org/:entityId/official/games_played',
  officialsGamesAssigned: '/reports/org/:entityId/official/games_assigned',
}

// Function to generate dynamic columns based on data
const generateDynamicColumns = (data, reportType) => {
  if (!data || !data.length) {
    return []
  }

  // Get all unique keys from the data
  const keys = Object.keys(data[0])

  return keys.map((key) => {
    // Special handling for URL field
    if (key === 'URL' || key === 'url') {
      return {
        title: 'URL',
        dataIndex: key,
        key: key,
        render: (item) => {
          if (!item) {
            return null
          }
          return (
            <a
              css={css`
                color: #ffffff;
                white-space: nowrap;
                width: 100%;
              `}
              href={item}
              target='_blank'
              rel='noreferrer'
            >
              <SearchOutlined />
            </a>
          )
        },
      }
    }

    // Referee notes run to hundreds of characters and keep their line breaks,
    // so wrap them in a fixed-width block instead of one unbroken line
    if (key === 'Referee Notes') {
      return {
        title: key,
        dataIndex: key,
        key: key,
        render: (item) => {
          return (
            <div
              css={css`
                color: ${colors.WHITE};
                white-space: pre-wrap;
                overflow-wrap: anywhere;
                width: 320px;
              `}
            >
              {item !== undefined && item !== null ? item : '-'}
            </div>
          )
        },
      }
    }

    // Special handling for date fields
    if (
      key === 'date' ||
      key === 'Date' ||
      key.includes('date') ||
      key.includes('Date')
    ) {
      return {
        // title: formatColumnTitle(key),
        title: key,
        dataIndex: key,
        key: key,
        render: (item) => {
          if (!item) {
            return null
          }
          return (
            <span
              css={css`
                color: #ffffff;
                white-space: nowrap;
                width: 100%;
              `}
            >
              {dayjs(item).isValid()
                ? dayjs(item).format('YYYY-MM-DD hh:mm A')
                : item}
            </span>
          )
        },
      }
    }

    // Special handling for boolean fields (like hasLogo in teamEngagement)
    if (reportType === 'teamEngagement' && key === 'hasLogo') {
      return {
        title: 'Has Logo',
        dataIndex: key,
        key: key,
        render: (item) => {
          return (
            <span
              css={css`
                color: #ffffff;
                white-space: nowrap;
                width: 100%;
              `}
            >
              {item ? 'Yes' : 'No'}
            </span>
          )
        },
      }
    }

    // Default rendering for all other fields
    return {
      // title: formatColumnTitle(key),
      title: key,
      dataIndex: key,
      key: key,
      render: (item) => {
        return (
          <span
            css={css`
              color: #ffffff;
              white-space: nowrap;
              width: 100%;
            `}
          >
            {item !== undefined && item !== null
              ? typeof item === 'object'
                ? JSON.stringify(item)
                : item
              : '-'}
          </span>
        )
      },
    }
  })
}

const generateHref = (string, reportName, ref) => {
  // Create formatted timestamp for filename
  const timestamp = dayjs().format('YYYYMMDDHHMMSS')
  // Format the filename: REPORTNAME_TIMESTAMP (all uppercase)
  const formattedFilename = `${reportName.toUpperCase()}_${timestamp}.csv`

  var blob = new Blob([string], { type: 'text/csv;charset=utf-8;' })

  if (navigator.msSaveBlob) {
    // IE 10+
    navigator.msSaveBlob(blob, formattedFilename)
  } else if (ref.current) {
    // const link = ref.current
    // Create a temporary anchor element if we can't use the ref directly for download trigger
    // But here we are just setting attributes on the hidden link or similar
    // The Button component doesn't expose the anchor element directly in the same way as a raw <a> tag
    // So we might need to handle download programmatically
    var url = URL.createObjectURL(blob)

    // Create a temporary link to trigger download
    const tempLink = document.createElement('a')
    tempLink.href = url
    tempLink.download = formattedFilename
    document.body.appendChild(tempLink)
    tempLink.click()
    document.body.removeChild(tempLink)
  }
}

const NewReports = ({ entityId, entityType }) => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width < 1024, [width])
  const [schedules, setSchedules] = useState([])
  const [selectedSchedule, setSelectedSchedule] = useState(null)
  const [schedulesDropdownOpen, setSchedulesDropdownOpen] = useState(false)
  const [schedule, setSchedule] = useState('ALL')
  const [report, setReport] = useState('penaltiesByTeam')
  const [loading, setLoading] = useState(false)
  const [csvString, setCSVString] = useState(false)
  const [columns, setColumns] = useState([])
  const [dateRange, setDateRange] = useState({
    starts_at: undefined,
    ends_at: undefined,
  })
  const [error, setError] = useState(null)
  const downloadRef = useRef(null)

  // Infinite loading state
  const [fullData, setFullData] = useState([])
  const [displayedData, setDisplayedData] = useState([])
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [isAppending, setIsAppending] = useState(false)

  const options = React.useMemo(() => {
    // Ensure registration-related reports are available at both org and competition levels
    return dropDownOptions
  }, [])

  useEffect(() => {
    const getSchedules = async () => {
      try {
        setLoading(true)
        const { data } = await req(
          `/organizations/${entityId}/schedules?sort=starts_at&direction=desc`
        )
        setSchedules(data || [])
      } catch (e) {
        console.error(e)
      } finally {
        setLoading(false)
      }
    }
    if (entityType === ENTITY_TYPES.org) {
      getSchedules()
    }
  }, [entityId, entityType])

  const getScheduleDisplayValue = useCallback(() => {
    if (!selectedSchedule) {
      return isCanlan ? 'All Seasons' : 'All Competitions'
    }
    return selectedSchedule.name_full || selectedSchedule.name
  }, [selectedSchedule])

  const scheduleOptions = useMemo(
    () => [
      {
        label: isCanlan ? 'All Seasons' : 'All Competitions',
        value: ALL_VALUE,
      },
      ...schedules.map((s) => {
        const dateRangeStr =
          s.starts_at && s.ends_at
            ? formatDateRange(s.starts_at, s.ends_at)
            : ''
        const displayName = s.name_full || s.name
        const imageUrl =
          s.image?.url || resolveAssetUrl(s.image) || COMPETITION_PLACEHOLDER

        return {
          value: s.id,
          searchText: displayName.toLowerCase(),
          label: (
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
              `}
            >
              <img
                src={imageUrl}
                alt={displayName}
                css={optionImageStyle}
                onError={(e) => {
                  e.currentTarget.onerror = null
                  e.currentTarget.src = COMPETITION_PLACEHOLDER
                }}
                loading='lazy'
              />
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  min-width: 0;
                  flex: 1;
                  gap: 2px;
                `}
              >
                <Text
                  variant='paragraphSmall'
                  weight='medium'
                  color={colors.NEUTRAL_0}
                  style={'line-height: 1;'}
                >
                  {displayName}
                </Text>
                {dateRangeStr && (
                  <Text
                    variant='paragraphXSmall'
                    color={colors.NEUTRAL_50}
                    style={'line-height: 1;'}
                  >
                    {dateRangeStr}
                  </Text>
                )}
              </div>
            </div>
          ),
        }
      }),
    ],
    [schedules]
  )

  const handleScheduleSelect = (value) => {
    setFullData([])
    setDisplayedData([])
    setColumns([])
    setError(null)
    if (value === ALL_VALUE) {
      setSelectedSchedule(null)
      setSchedule('ALL')
    } else {
      const found = schedules.find((s) => s.id === value)
      setSelectedSchedule(found || null)
      setSchedule(value || 'ALL')
    }
  }

  const onView = async () => {
    try {
      setError(null)
      setLoading(true)
      setFullData([])
      setDisplayedData([])
      setPage(1)
      setHasMore(false)

      const query = {}
      if (dateRange.starts_at && dateRange.ends_at) {
        query.starts_after = dayjs(dateRange.starts_at)
          .hour(0)
          .minute(0)
          .second(0)
          .format()
        query.starts_before = dayjs(dateRange.ends_at)
          .hour(0)
          .minute(0)
          .second(0)
          .add(1, 'day')
          .format()
      }
      query.format = 'csv'
      // Add the report type to the query
      if (report === 'registrationRevenue') {
        query.report_type = 'revenue'
      } else if (report === 'registrationTransactions') {
        query.report_type = 'transaction'
      } else if (report === 'teamRegistrations') {
        query.report_type = 'team_registrations'
      } else if (report === 'playerRegistrations') {
        query.report_type = 'player_registrations'
      } else if (report === 'registrations') {
        query.report_type = 'info'
      } else if (report === 'financialSummary') {
        query.report_type = 'financial_summary'
      } else if (report === 'pastDuePayments') {
        query.report_type = 'delinquent'
      } else if (report === 'incompleteRegistrations') {
        query.report_type = 'incomplete_sessions'
      }
      let url = ''
      if (entityType === ENTITY_TYPES.org) {
        if (schedule === 'ALL') {
          url = reportOrgEndpoints[report].replace(':entityId', entityId)
        } else {
          url = reportEndpoints[report].replace(':entityId', schedule)
        }
      } else {
        url = reportEndpoints[report].replace(':entityId', entityId)
      }
      const response = await req(url, { parseJSON: false, query })
      const text = await response.text()
      setCSVString(text)

      const result = await csv({ output: 'json', flatKeys: true }).fromString(
        text
      )

      // Use the generic transform function for all report types
      const transformedData = transformData(result, report)
      setFullData(transformedData)

      // Generate dynamic columns based on the transformed data
      if (transformedData && transformedData.length > 0) {
        const dynamicColumns = generateDynamicColumns(transformedData, report)
        setColumns(dynamicColumns)

        // Initial load of displayed data
        const initialData = transformedData.slice(0, PAGE_SIZE)
        setDisplayedData(initialData)
        setHasMore(transformedData.length > PAGE_SIZE)
      } else {
        setColumns([])
        setDisplayedData([])
        setHasMore(false)
      }
    } catch (e) {
      console.error(e)
      setError(
        `There was an error generating the report. ${
          e?.message || ''
        } Please try again.`
      )
    } finally {
      setLoading(false)
    }
  }

  const loadMoreData = useCallback(() => {
    if (isAppending || !hasMore) {
      return
    }

    setIsAppending(true)

    // Simulate a small delay for better UX or just process immediately
    // Since it's client side slicing, it's fast.
    const nextPage = page + 1
    const startIndex = (nextPage - 1) * PAGE_SIZE
    const endIndex = startIndex + PAGE_SIZE
    const nextBatch = fullData.slice(startIndex, endIndex)

    if (nextBatch.length > 0) {
      setDisplayedData((prev) => [...prev, ...nextBatch])
      setPage(nextPage)
      setHasMore(endIndex < fullData.length)
    } else {
      setHasMore(false)
    }

    setIsAppending(false)
  }, [page, fullData, hasMore, isAppending])

  const handleScroll = useCallback(() => {
    const { scrollTop, scrollHeight, clientHeight } = document.documentElement
    const nearBottom =
      scrollHeight - (scrollTop + clientHeight) < SCROLL_THRESHOLD
    if (nearBottom && hasMore && !loading && !isAppending) {
      loadMoreData()
    }
  }, [hasMore, loading, isAppending, loadMoreData])

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [handleScroll])

  return (
    <div
      css={css`
        margin: 24px auto;
        display: flex;
        flex-direction: column;
      `}
    >
      <div
        css={css`
          display: flex;
          flex-direction: row;
          justify-content: flex-start;
          align-items: flex-end;
          gap: 16px;
          margin-bottom: 16px;
          width: 100%;
          flex-wrap: wrap;
          @media (max-width: 768px) {
            padding: 0 16px;
          }
        `}
      >
        <div
          css={css`
            flex: 1 1 180px;
            min-width: 180px;
            display: flex;
            flex-direction: column;
            gap: 8px;
            @media (max-width: 768px) {
              flex: 1 1 100%;
              min-width: 0;
            }
          `}
        >
          <SelectInput
            label={t('util:capitalize', { text: 'Report Type' })}
            value={report}
            onChange={(selectedReport) => {
              setFullData([])
              setDisplayedData([])
              setColumns([])
              setError(null)
              setReport(selectedReport)
            }}
            options={options}
            disabled={loading}
            name='reportType'
            fieldErrors={{}}
            customError={null}
            showSearch
            popupMatchSelectWidth={false}
            backgroundColor='transparent'
            style={css`
              width: 100%;
              background-color: transparent;
            `}
            allowClear={false}
          />
        </div>

        {entityType === ENTITY_TYPES.org ? (
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              gap: 8px;
              flex: 1 1 180px;
              min-width: 180px;
              @media (max-width: 768px) {
                flex: 1 1 100%;
                min-width: 0;
              }
            `}
          >
            {/* Global styles for AutoComplete dropdown */}
            <style>
              {`
                .reports-schedule-dropdown {
                  z-index: 999999 !important;
                  background-color: ${colors.SECONDARY_500} !important;
                  padding-top: 4px;
                  padding-left: 0;
                  padding-right: 0;
                  padding-bottom: 0;
                }
                .reports-schedule-dropdown .ant-select-item-option-content {
                  font-size: 14px !important;
                  font-weight: 400 !important;
                  color: ${colors.WHITE} !important;
                }
              `}
            </style>
            <Text
              variant='paragraphSmall'
              weight='regular'
              color={colors.WHITE}
              style={`
                align-self: stretch;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
              `}
            >
              {isCanlan ? 'Season' : 'Competition'}
            </Text>
            <AutoComplete
              popupMatchSelectWidth={false}
              css={filterAutoComplete}
              popupClassName='reports-schedule-dropdown'
              value={getScheduleDisplayValue()}
              placeholder={isCanlan ? 'All Seasons' : 'All Competitions'}
              options={scheduleOptions}
              onSelect={handleScheduleSelect}
              filterOption={false}
              allowClear={false}
              onClear={() => {
                setSelectedSchedule(null)
                setSchedule('ALL')
                setFullData([])
                setDisplayedData([])
                setColumns([])
                setError(null)
              }}
              disabled={loading}
              open={schedulesDropdownOpen}
              onDropdownVisibleChange={setSchedulesDropdownOpen}
              suffixIcon={
                <DownOutlined
                  style={{ color: colors.NEUTRAL_50, cursor: 'pointer' }}
                  onClick={() => setSchedulesDropdownOpen((prev) => !prev)}
                />
              }
              notFoundContent={
                loading ? (
                  <div
                    css={css`
                      display: flex;
                      justify-content: center;
                      padding: 8px;
                    `}
                  >
                    <Spin size='small' />
                  </div>
                ) : (
                  <Text
                    variant='paragraphSmall'
                    weight='regular'
                    color={colors.NEUTRAL_50}
                  >
                    No competitions found
                  </Text>
                )
              }
            />
          </div>
        ) : null}

        <div
          css={css`
            flex: 1 1 260px;
            min-width: 260px;
            @media (max-width: 768px) {
              flex: 1 1 100%;
              min-width: 0;
            }
          `}
        >
          <RangePicker
            label='Date Range'
            starts_at={dateRange.starts_at}
            ends_at={dateRange.ends_at}
            onChange={(dates) => {
              try {
                if (dates) {
                  const [start, end] = dates
                  setDateRange({
                    starts_at: start,
                    ends_at: end,
                  })
                } else {
                  setDateRange({
                    starts_at: undefined,
                    ends_at: undefined,
                  })
                }
              } catch (e) {
                console.error(e)
              }
            }}
            fieldErrors={[]}
            name='dateRange'
            customError={null}
            allowClear={true}
            backgroundColor='transparent'
          />
        </div>

        <div
          css={css`
            display: flex;
            gap: 16px;
            align-items: flex-end;
            flex: 1 1 320px;
            min-width: 320px;
            @media (max-width: 768px) {
              flex: 1 1 100%;
              min-width: 0;
            }
          `}
        >
          <Button
            text='View'
            onClick={onView}
            isLoading={loading}
            variant='primary'
            style={css`
              width: 150px;
              white-space: nowrap;
              @media (max-width: 768px) {
                flex: 1;
                width: auto;
              }
            `}
            icon={
              <Icon
                name='search'
                css={css`
                  margin-right: 6px;
                  transform: translateY(-1px);
                `}
                fontSize={14}
              />
            }
            disabled={!report}
          />

          <Button
            text='Download CSV'
            onClick={() => generateHref(csvString, report, downloadRef)}
            disabled={!csvString || fullData.length === 0}
            variant='secondary'
            style={css`
              width: 150px;
              white-space: nowrap;
              @media (max-width: 768px) {
                flex: 1;
                width: auto;
              }
            `}
            icon={
              <Icon
                name='download'
                css={css`
                  margin-right: 6px;
                `}
                fontSize={14}
              />
            }
          />
          {/* Hidden ref for download if needed, though we are using programmatic download now */}
          <div
            ref={downloadRef}
            css={css`
              display: none;
            `}
          />
        </div>
      </div>

      {error && (
        <Alert
          showIcon
          message={error}
          type='error'
          css={css`
            margin-bottom: 16px;
            width: 100%;
          `}
          onClose={() => setError(null)}
        />
      )}

      {loading && displayedData.length === 0 ? (
        <div
          css={css`
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 200px;
          `}
        >
          <Spin
            size='small'
            css={css`
              .ant-spin-dot-item {
                background-color: ${colors.PRIMARY};
              }
            `}
          />
        </div>
      ) : (
        <Table
          css={[
            getTableStyles(isMobile),
            css`
              overflow-x: auto;
              width: 100%;
            `,
          ]}
          columns={
            columns.length > 0
              ? columns.map((column) => ({
                  ...column,
                  // Add ellipsis property to handle text overflow in column headers
                  ellipsis: false, // Changed to false to prevent truncation
                  // Add a custom title render function to ensure single line
                  title: (
                    <div
                      css={css`
                        white-space: nowrap;
                        overflow: visible;
                        text-overflow: unset;
                      `}
                    >
                      {column.title}
                    </div>
                  ),
                }))
              : []
          }
          dataSource={displayedData}
          bordered
          size='small'
          pagination={false}
        />
      )}
    </div>
  )
}

export default NewReports
